import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/client.js';
import { addDays } from '../lib/dates.js';

/** Days fetched either side of the selected date, so navigation feels instant. */
const PREFETCH_RADIUS = 3;

/** Fallback poll interval used when the event stream is unavailable. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Appointments for the selected day.
 *
 * Fetches a small window around the date rather than the whole register — the
 * spreadsheet version downloaded every appointment ever recorded on each of its
 * 45-second refreshes, which does not scale past a year or two of data.
 *
 * Freshness comes from a Server-Sent Events stream, so an edit in the office
 * appears on the governor's screen in about a second. The poll is a safety net
 * for when the stream is blocked or drops.
 */
export function useMeetings(selectedDate) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Cache keyed by date, so stepping back to a visited day renders immediately.
  const cacheRef = useRef(new Map());
  const requestIdRef = useRef(0);
  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;

  const load = useCallback(async (date, { quiet = false } = {}) => {
    const requestId = ++requestIdRef.current;
    if (!quiet) setLoading(true);

    const from = addDays(date, -PREFETCH_RADIUS);
    const to = addDays(date, PREFETCH_RADIUS);

    try {
      const data = await api.getMeetingsByRange(from, to);

      // A slower earlier request must not overwrite a newer one's result.
      if (requestId !== requestIdRef.current) return;

      const byDate = new Map();
      for (let offset = -PREFETCH_RADIUS; offset <= PREFETCH_RADIUS; offset++) {
        byDate.set(addDays(date, offset), []);
      }
      for (const meeting of data.meetings) {
        if (!byDate.has(meeting.date)) byDate.set(meeting.date, []);
        byDate.get(meeting.date).push(meeting);
      }

      cacheRef.current = byDate;
      setMeetings(byDate.get(selectedDateRef.current) || []);
      setError('');
    } catch (caught) {
      if (requestId !== requestIdRef.current) return;
      setError(caught?.message || 'تعذّر تحميل المواعيد');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  /* Selected date changed: show cached data at once, then refresh. */
  useEffect(() => {
    const cached = cacheRef.current.get(selectedDate);
    if (cached) {
      setMeetings(cached);
      setLoading(false);
      // Still refetch quietly — the window has moved and the edges are unknown.
      load(selectedDate, { quiet: true });
    } else {
      load(selectedDate);
    }
  }, [selectedDate, load]);

  const refresh = useCallback(
    () => load(selectedDateRef.current, { quiet: true }),
    [load]
  );

  /* Live updates. */
  useEffect(() => {
    let source;
    let streamHealthy = false;

    try {
      source = new EventSource('/api/events');

      source.addEventListener('connected', () => {
        streamHealthy = true;
      });

      source.addEventListener('meetings-changed', () => {
        refresh();
      });

      source.onerror = () => {
        // EventSource reconnects on its own; mark unhealthy so the poll covers
        // the gap in the meantime.
        streamHealthy = false;
      };
    } catch {
      streamHealthy = false;
    }

    const poll = setInterval(() => {
      if (!streamHealthy) refresh();
    }, POLL_INTERVAL_MS);

    // A screen left open overnight should be correct the moment it is looked at
    // again, without waiting for the next poll.
    function handleVisibility() {
      if (document.visibilityState === 'visible') refresh();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      source?.close();
      clearInterval(poll);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refresh]);

  return { meetings, loading, error, refresh };
}

export default useMeetings;
